/*
 * Alarme da página de votação: som (Web Audio, sem arquivo), vibração e
 * "manter a tela acesa" (Wake Lock). Tudo com APIs nativas do navegador.
 *
 * Limites conhecidos:
 *  - Som só toca depois de um toque do usuário na página (regra dos navegadores).
 *    Por isso chamamos unlockAudio() nos botões de entrada.
 *  - iPhone não vibra via navegador; com o botão "silencioso" ligado, fica mudo.
 *  - Com a tela apagada o navegador pausa o JS: o alarme toca ao desbloquear.
 */

const SOUND_KEY = 'voteSoundOn';

let ctx = null;
let wakeLock = null;

export function isSoundOn() {
  return localStorage.getItem(SOUND_KEY) !== '0';
}

export function setSoundOn(on) {
  localStorage.setItem(SOUND_KEY, on ? '1' : '0');
}

function getCtx() {
  if (ctx) return ctx;
  const AC = window.AudioContext || window.webkitAudioContext;
  if (!AC) return null;
  try {
    ctx = new AC();
  } catch (e) {
    ctx = null;
  }
  return ctx;
}

// Deve ser chamado dentro de um evento de toque/clique do usuário.
export function unlockAudio() {
  const c = getCtx();
  if (!c) return;
  if (c.state === 'suspended') c.resume().catch(() => {});
  // Toca um buffer silencioso para "destravar" o áudio no iOS
  try {
    const buf = c.createBuffer(1, 1, 22050);
    const src = c.createBufferSource();
    src.buffer = buf;
    src.connect(c.destination);
    src.start(0);
  } catch (e) { /* ignora */ }
}

function tone(c, freq, start, duration, gainValue = 0.25) {
  const osc = c.createOscillator();
  const gain = c.createGain();
  osc.type = 'sine';
  osc.frequency.setValueAtTime(freq, start);
  gain.gain.setValueAtTime(0, start);
  gain.gain.linearRampToValueAtTime(gainValue, start + 0.02);
  gain.gain.setValueAtTime(gainValue, start + duration - 0.05);
  gain.gain.linearRampToValueAtTime(0, start + duration);
  osc.connect(gain);
  gain.connect(c.destination);
  osc.start(start);
  osc.stop(start + duration + 0.01);
}

export function vibrate(pattern) {
  try {
    if (navigator.vibrate) navigator.vibrate(pattern);
  } catch (e) { /* ignora */ }
}

/*
 * strong = false → bipe curto (lembrete)
 * strong = true  → sequência longa (abertura da votação / chamada da secretaria)
 */
export function playAlarm({ strong = false } = {}) {
  vibrate(strong ? [400, 150, 400, 150, 600] : [250, 100, 250]);
  if (!isSoundOn()) return;
  const c = getCtx();
  if (!c) return;
  if (c.state === 'suspended') c.resume().catch(() => {});
  const t0 = c.currentTime + 0.02;
  if (strong) {
    // três pares de notas ascendentes
    for (let i = 0; i < 3; i++) {
      const base = t0 + i * 0.55;
      tone(c, 880, base, 0.18, 0.3);
      tone(c, 1175, base + 0.2, 0.25, 0.3);
    }
  } else {
    tone(c, 988, t0, 0.15, 0.22);
    tone(c, 1319, t0 + 0.18, 0.2, 0.22);
  }
}

// ---------- Wake Lock (manter a tela acesa) ----------

export async function requestWakeLock() {
  if (!('wakeLock' in navigator)) return false;
  if (wakeLock) return true;
  try {
    wakeLock = await navigator.wakeLock.request('screen');
    wakeLock.addEventListener('release', () => { wakeLock = null; });
    return true;
  } catch (e) {
    wakeLock = null;
    return false;
  }
}

export function releaseWakeLock() {
  if (!wakeLock) return;
  try { wakeLock.release(); } catch (e) { /* ignora */ }
  wakeLock = null;
}

export function hasWakeLock() {
  return !!wakeLock;
}
